/**
 * applyEffectPost — run multi-pass shadow loops or WebGL shader pipelines
 * after the initial drawElementImage has placed the source pixels on the
 * canvas at (offsetX, offsetY) within ctx's current transform.
 *
 * Called once per frame inside the unified CanvasSurface.tsx paint loop.
 *
 * Coordinate model:
 *  - `ctx` already has the frame's world transform applied (translate +
 *    scale). The "main" drawElementImage call drew the element at (0,0) in
 *    that local space.
 *  - Shadow effects re-issue drawElementImage in the same local space.
 *  - Shader effects produce an offscreen WebGL canvas of size (w, h) in CSS
 *    pixels; we composite back via ctx.drawImage(glCanvas, 0, 0, w, h).
 *
 * Effects that are pure ctx.filter setters (blur, grayscale) are no-ops
 * here — applyEffectPre handled them.
 */
import type { Effect } from '../store/types';
import { runCrt, runChromatic, runFrostedGlass } from './shaders/shaderRunner';
import { parseColor } from './parseColor';

interface Ctx2DWithDrawElement extends CanvasRenderingContext2D {
  drawElementImage?: (el: Element, x: number, y: number) => DOMMatrix;
}

export interface ApplyEffectPostOpts {
  /** Frame width in CSS px (used to size shader staging canvases). */
  w: number;
  /** Frame height in CSS px. Falls back to el.offsetHeight if undefined. */
  h?: number;
}

export function applyEffectPost(
  ctx: CanvasRenderingContext2D,
  effect: Effect,
  el: Element,
  opts: ApplyEffectPostOpts,
): void {
  const dctx = ctx as Ctx2DWithDrawElement;
  const w = Math.max(1, Math.round(opts.w));
  const h = Math.max(1, Math.round(opts.h ?? (el as HTMLElement).offsetHeight ?? opts.w));

  switch (effect.kind) {
    case 'none':
    case 'blur':
    case 'grayscale':
      return;

    case 'outline':
      runOutline(dctx, el, effect.color, Math.max(0.5, effect.width));
      return;

    case 'glow':
      runGlow(dctx, el, effect.color, Math.max(0, effect.radius), Math.max(1, effect.passes));
      return;

    case 'crt': {
      const out = runCrt(el, w, h, { intensity: effect.intensity });
      if (out) compositeBack(ctx, out, w, h);
      return;
    }

    case 'chromatic': {
      const out = runChromatic(el, w, h, { offset: effect.offset });
      if (out) compositeBack(ctx, out, w, h);
      return;
    }

    case 'frostedGlass': {
      const out = runFrostedGlass(el, w, h, {
        blur: effect.blur,
        tint: parseColor(effect.tint),
      });
      if (out) compositeBack(ctx, out, w, h);
      return;
    }
  }
}

/**
 * 8-direction shadow pass for outline. After the main draw, we re-issue
 * drawElementImage 8 times with shadowOffset shifted by +/- width in each
 * compass direction. ctx.shadowBlur=0 keeps the edges crisp.
 */
function runOutline(
  ctx: Ctx2DWithDrawElement,
  el: Element,
  color: string,
  width: number,
): void {
  if (typeof ctx.drawElementImage !== 'function') return;
  const prevShadowColor = ctx.shadowColor;
  const prevShadowBlur = ctx.shadowBlur;
  const prevShadowX = ctx.shadowOffsetX;
  const prevShadowY = ctx.shadowOffsetY;
  ctx.shadowColor = color;
  ctx.shadowBlur = 0;

  const dirs: Array<[number, number]> = [
    [-1, -1], [0, -1], [1, -1],
    [-1, 0], [1, 0],
    [-1, 1], [0, 1], [1, 1],
  ];
  for (const [dx, dy] of dirs) {
    ctx.shadowOffsetX = dx * width;
    ctx.shadowOffsetY = dy * width;
    try {
      ctx.drawElementImage(el, 0, 0);
    } catch {
      /* paint-record gap — recover next tick */
    }
  }

  ctx.shadowColor = prevShadowColor;
  ctx.shadowBlur = prevShadowBlur;
  ctx.shadowOffsetX = prevShadowX;
  ctx.shadowOffsetY = prevShadowY;
}

/**
 * Multi-pass shadow for glow. Stack `passes` drawElementImage calls each
 * dropping a soft shadow under the source. Loop count escalates intensity
 * without needing WebGL.
 */
function runGlow(
  ctx: Ctx2DWithDrawElement,
  el: Element,
  color: string,
  radius: number,
  passes: number,
): void {
  if (typeof ctx.drawElementImage !== 'function') return;
  const prevShadowColor = ctx.shadowColor;
  const prevShadowBlur = ctx.shadowBlur;
  const prevShadowX = ctx.shadowOffsetX;
  const prevShadowY = ctx.shadowOffsetY;
  ctx.shadowColor = color;
  ctx.shadowBlur = radius;
  ctx.shadowOffsetX = 0;
  ctx.shadowOffsetY = 0;

  const n = Math.min(8, Math.max(1, Math.round(passes)));
  for (let i = 0; i < n; i++) {
    try {
      ctx.drawElementImage(el, 0, 0);
    } catch {
      /* paint-record gap */
    }
  }

  ctx.shadowColor = prevShadowColor;
  ctx.shadowBlur = prevShadowBlur;
  ctx.shadowOffsetX = prevShadowX;
  ctx.shadowOffsetY = prevShadowY;
}

/**
 * Draw the shader output canvas back into the main canvas at the same local
 * (0,0). We disable ctx.filter for the composite so the cheap pre-pass
 * approximation doesn't double up with the shader result.
 */
function compositeBack(
  ctx: CanvasRenderingContext2D,
  glCanvas: HTMLCanvasElement,
  w: number,
  h: number,
): void {
  const prevFilter = ctx.filter;
  ctx.filter = 'none';
  ctx.drawImage(glCanvas, 0, 0, w, h);
  ctx.filter = prevFilter;
}
